import React, { useState } from "react";
import BSButton from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";
import { PencilSquare } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import {
  useAddOrUpdateRejectionReasonMutation,
  useGetRejectionReasonsListQuery,
} from "features/api/contentManagement/contentManagementApiSlice";
import { DBRejectionReason } from "models/RejectionReason";
import RenderList from "./List/RenderList";
import AddContentForm from "./AddContentForm";
import { Button } from "./Button";

import {
  RejectionReasonFormControlsValues,
  rejectionReasonsTabFields,
} from "../contentManagementUtils/tabSchema";

const RejectionReasonsTab = () => {
  const { t } = useTranslation();
  const { data: tableData } = useGetRejectionReasonsListQuery();
  const [addOrUpdateRejectionReason] = useAddOrUpdateRejectionReasonMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] =
    useState<DBRejectionReason | null>(null);

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: DBRejectionReason | null) => {
    setShowUpdateForm(initialValues);
  };

  const validationSchema: Yup.ObjectSchema<RejectionReasonFormControlsValues> =
    Yup.object().shape({
      name: Yup.string().required(t("חובה להזין שם סיבת פסילה")),
      disable: Yup.boolean().default(false),
    });

  const initialValues: DBRejectionReason = {
    id: 0,
    name: "",
    disable: false,
    isEditable: true,
  };
  const handleCreateOrUpdate: (values: DBRejectionReason) => void = async (
    // eslint-disable-next-line prettier/prettier
    values: DBRejectionReason
  ) => {
    const payload = {
      ID: values.id ?? 0,
      Name: values.name ?? "",
      Disable: values.disable ?? false,
    };

    await addOrUpdateRejectionReason(payload).unwrap();

    values?.id ? toggleUpdateForm(null) : toggleCreateForm();
  };

  const handleDisableChange = async (reasonCode: number, disable: boolean) => {
    const reason = tableData?.find((r) => r.id === reasonCode);
    if (!reason) return;

    const payload = {
      ID: reason.id,
      Name: reason.name,
      Disable: disable,
    };

    await addOrUpdateRejectionReason(payload).unwrap();
  };

  const columns = [
    {
      title: t("קוד סיבת פסילה"),
      dataIndex: 1,
      key: "id",
      flex: 1,
      renderCell: (text: string | number) => <span>{text}</span>,
      asc: true,
      minWidth: 140,
    },
    {
      title: t("שם סיבת פסילה"),
      dataIndex: 2,
      key: "name",
      flex: 2,
      renderCell: (text: string | number) => <span>{text}</span>,
      asc: true,
      minWidth: 180,
    },
    {
      title: t("חסום?"),
      dataIndex: 3,
      key: "actions",
      flex: 1,
      renderCell: (reason: DBRejectionReason) => {
        const isLockedReason = reason.isEditable === false;

        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id={`rejection-reason-disable-${reason.id}`}
              checked={reason.disable}
              disabled={isLockedReason}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleDisableChange(reason.id, event.target.checked);
              }}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 140,
    },
    {
      title: "",
      dataIndex: 4,
      key: "actions",
      flex: 1,
      renderCell: (reason: DBRejectionReason) => {
        const isLockedReason = reason.isEditable === false;

        return (
          <Stack direction="horizontal" gap={0}>
            <div className="button-wrapper">
              <BSButton
                key={reason.id}
                name={reason.name}
                variant="outline"
                disabled={isLockedReason}
                style={isLockedReason ? { border: "none", opacity: 0.5 } : {}}
              >
                <PencilSquare
                  size={20}
                  color={isLockedReason ? "gray" : "primary"}
                  onClick={() => {
                    if (!isLockedReason) {
                      toggleUpdateForm(reason);
                    }
                  }}
                />
              </BSButton>
            </div>
          </Stack>
        );
      },
      asc: true,
      minWidth: 100,
      maxWidth: 120,
    },
  ];

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>

      <RenderList<DBRejectionReason> data={tableData} columns={columns} />

      <AddContentForm
        isOpen={showCreateForm}
        toggle={toggleCreateForm}
        fields={rejectionReasonsTabFields}
        submitForm={handleCreateOrUpdate}
        headerTitle={t("סיבת פסילה חדשה")}
        approveBtnLabel={t("יצירת סיבת פסילה")}
        cancelBtnLabel={t("cancel")}
        initialValues={initialValues}
        validationSchema={validationSchema}
      />

      <AddContentForm
        isOpen={!!showUpdateForm}
        toggle={toggleUpdateForm}
        fields={rejectionReasonsTabFields}
        submitForm={handleCreateOrUpdate}
        headerTitle={t("עדכן סיבת פסילה")}
        approveBtnLabel={t("עדכן סיבת פסילה")}
        cancelBtnLabel={t("cancel")}
        initialValues={showUpdateForm}
        validationSchema={validationSchema}
      />
    </div>
  );
};

export default RejectionReasonsTab;
